import { nafColor } from '@nafcore/theme';
import { useMemo } from 'react';
import { Season } from '@appTypes/ElPrixContextData';
import { escapeHtml } from '@utils/maps/htmlSanitizer';
import { PointOfInterest } from './RouteCharts';
import { createRangeChart, createRouteChart, RangeData } from './ElevationChart.utils';

interface ElevationChartOptions {
  altitudes: number[];
  pointsOfInterest: PointOfInterest[];
  rangeData?: RangeData;
  display: 'route' | 'range';
  season?: Season;
  testDate?: string;
}

export const useElevationChartOptions = ({
  altitudes,
  pointsOfInterest,
  rangeData,
  display,
  season,
  testDate,
}: ElevationChartOptions) => {
  const repeatRoute = pointsOfInterest.find((point) => point.title === 'repeat');

  return useMemo(
    (): Highcharts.Options => ({
      plotOptions: {
        area: { pointStart: 0 },
        series: {
          stickyTracking: false,
          states: { inactive: { opacity: 1 }, hover: { enabled: false } },
        },
      },
      chart: { zooming: { type: 'x' } },
      tooltip: {
        snap: 10,
        useHTML: true,
        shadow: false,
        shared: true,
        formatter: function formatter() {
          const { x, y, key } = this;

          // Highcharts wants a markup string. Built directly rather than via `renderToString`,
          // which pulled the whole 2.1 MB react-dom server renderer into the browser bundle to
          // produce four elements of static markup.
          const heading = key ? `<b>${escapeHtml(String(key))}</b>` : '';
          const distance = x === 0 ? 'Start' : `${escapeHtml(String(x))} km`;

          return `<div>${heading}<div><p>${distance}</p><p>${escapeHtml(String(y))} moh.</p></div></div>`;
        },
        borderWidth: 0,
        padding: 0,
      },
      title: undefined,
      xAxis: {
        plotLines:
          display === 'range' && rangeData
            ? [
                {
                  color: season === 'winter' ? nafColor.information.information : nafColor.information.successDark,
                  width: 1,
                  value: rangeData.measured,
                  dashStyle: 'Dash',
                  zIndex: 2,
                },
                {
                  color: season === 'winter' ? nafColor.information.information : nafColor.information.successDark,
                  width: 1,
                  value: rangeData.stated,
                  dashStyle: 'Dash',
                  zIndex: 2,
                },
              ]
            : undefined,
        plotBands: repeatRoute
          ? [
              {
                from: repeatRoute.kmFromStart,
                to: altitudes.length - 1,
                label: { style: { color: nafColor.neutral.neutral4 }, text: 'SLØYFEN GJENTAS' },
                color: nafColor.signature.white,
                borderColor: nafColor.neutral.neutral3,
                borderWidth: 1,
              },
            ]
          : [],
        tickInterval: 50,
        labels: { formatter: ({ value }) => `${value}km` },
        title: { text: undefined },
        accessibility: { rangeDescription: 'Range: 0 to 486km' },
        crosshair: {
          dashStyle: 'Dash',
          color: season === 'winter' ? nafColor.information.information : nafColor.information.successDark,
          zIndex: 6,
        },
      },
      yAxis: {
        startOnTick: true,
        endOnTick: false,
        maxPadding: 0.35,
        lineWidth: 1,
        gridLineDashStyle: 'Dash',
        title: { text: undefined },
        labels: { format: '{value} m' },
        accessibility: { description: 'Elevation', rangeDescription: 'Range: 0 to 1,053 meters' },
      },
      credits: { enabled: false },
      legend: { enabled: false },
      series:
        display === 'range'
          ? createRangeChart({
              altitudes,
              rangeData,
              repeatPoint: repeatRoute?.kmFromStart ?? Math.min(altitudes.length, 350),
              season,
              testDate,
              pointsOfInterest,
            })
          : createRouteChart(altitudes, pointsOfInterest, season),
    }),
    [altitudes, pointsOfInterest, rangeData, display, repeatRoute, season, testDate],
  );
};
